import React from 'react'
import {
    Theme,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    makeStyles,
} from '@material-ui/core'
import { Container } from '../../../../components'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { getEntityClassDisplayName } from '../../../../models/DocumentAudit/services'
import {
    DeliverableValue,
    ResidentTermDirectoryDetail,
    CriteriaGroup,
    DeliverableValueStatusConfig,
    EntityValue,
} from '../../../../models/DocumentAudit'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import TableChartIcon from '@material-ui/icons/TableChart'
import ChatIcon from '@material-ui/icons/Chat'

interface Props {
    criteriaGroup: CriteriaGroup | undefined
    row: ResidentTermDirectoryDetail | null
    entityValue: DeliverableValue | null
    onEntitySelect: (entityValue: DeliverableValue) => void
    onDocumentSelect: (
        documentId: number,
        pageNumber: number | null,
        valueId: number,
        coordinates: string | null,
    ) => void
    theme: Theme
    selectedDocumentId: number | null
    selectedPage: number | null
    selectedValueId: number | undefined
    selectedCoordinates: string | null
    onOpenChat: (
        channelId: number,
        entityTitle: string,
        event: React.MouseEvent<HTMLElement>,
    ) => void
}

const useStyles = makeStyles((theme) => ({
    entityRow: {
        padding: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))

const SupportingValue = ({
    value,
    theme,
    onValueClick,
    isSelected,
}: {
    value: EntityValue
    theme: Theme
    onValueClick: (
        documentId: number,
        pageNumber: number | null,
        valueId: number,
        coordinates: string | null,
    ) => void
    isSelected: boolean
}) => (
    <Container
        onClick={() =>
            onValueClick(
                value.document.id,
                value.page_number,
                value.id,
                value.coordinates,
            )
        }
        style={{
            flexDirection: 'column',
            gap: theme.spacing(1),
            padding: theme.spacing(1),
            backgroundColor: theme.palette.action.hover,
            borderRadius: theme.shape.borderRadius,
            cursor: 'pointer',
            borderLeft: isSelected
                ? `4px solid ${theme.palette.primary.main}`
                : '4px solid transparent',
            transition: 'all 0.2s ease',
        }}
    >
        <Container
            style={{
                flexDirection: 'row',
                gap: theme.spacing(2),
                alignItems: 'center',
            }}
        >
            {value.document.document_type === 'PDF' ? (
                <PictureAsPdfIcon
                    style={{
                        fontSize: 16,
                        color: theme.palette.text.secondary,
                    }}
                />
            ) : (
                <TableChartIcon
                    style={{
                        fontSize: 16,
                        color: theme.palette.text.secondary,
                    }}
                />
            )}
            <Typography
                variant="body2"
                style={{
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {value.value || 'No value'}
            </Typography>
            <Typography
                variant="caption"
                style={{
                    flexShrink: 0,
                    color: theme.palette.text.secondary,
                }}
            >
                {value.document.document_type === 'EXCEL'
                    ? value.coordinates
                    : value.page_number || 'Unknown'}
            </Typography>
        </Container>
        <Typography
            variant="caption"
            style={{
                color: theme.palette.text.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {value.document.file_name}
        </Typography>
    </Container>
)

const EntityListItem = ({
    criteria,
    matchingValue,
    entityValue,
    onEntitySelect,
    theme,
    row,
    onDocumentSelect,
    selectedDocumentId,
    selectedPage,
    selectedValueId,
    selectedCoordinates,
    onOpenChat,
}: {
    criteria: CriteriaGroup['match_criteria'][0]
    matchingValue: DeliverableValue | undefined
    entityValue: DeliverableValue | null
    onEntitySelect: (entityValue: DeliverableValue) => void
    theme: Theme
    row: ResidentTermDirectoryDetail | null
    onDocumentSelect: (
        documentId: number,
        pageNumber: number | null,
        valueId: number,
        coordinates: string | null,
    ) => void
    selectedDocumentId: number | null
    selectedPage: number | null
    selectedValueId?: number
    selectedCoordinates?: string | null
    onOpenChat?: (
        channelId: number,
        entityTitle: string,
        event: React.MouseEvent<HTMLElement>,
    ) => void
}) => {
    const classes = useStyles()
    const statusConfig =
        matchingValue && DeliverableValueStatusConfig[matchingValue.status]
    const StatusIcon = statusConfig?.icon.component

    const supportingValues =
        row?.documents.flatMap((doc) => {
            return doc.entity_values.filter((v) => {
                return (
                    v.entity_alias.entity_class?.id === criteria.entity_class.id
                )
            })
        }) || []

    const handleOpenChat = (
        value: DeliverableValue,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        if (value.channel && onOpenChat) {
            onOpenChat(value.channel, value.entity_classification.name, event)
        }
    }

    return (
        <Accordion
            key={criteria.id}
            expanded={matchingValue?.id === entityValue?.id}
            onChange={() => matchingValue && onEntitySelect(matchingValue)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.entityRow}
                style={{
                    backgroundColor: statusConfig?.backgroundColor,
                }}
            >
                <Container
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Typography>
                        {getEntityClassDisplayName(criteria.entity_class.name)}
                    </Typography>
                    {matchingValue && (
                        <div
                            style={{
                                marginLeft: 'auto',
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                            }}
                        >
                            {onOpenChat && (
                                <Container
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleOpenChat(matchingValue, e)
                                    }}
                                    style={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <ChatIcon
                                        style={{
                                            fontSize: 16,
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                    {matchingValue.message_count > 0 && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: -8,
                                                right: -8,
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                color:
                                                    theme.palette.primary
                                                        .contrastText,
                                                borderRadius: '50%',
                                                padding: '2px 6px',
                                                fontSize: '0.75rem',
                                                lineHeight: 1,
                                                minWidth: '16px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {matchingValue.message_count}
                                        </div>
                                    )}
                                </Container>
                            )}
                            {StatusIcon && (
                                <StatusIcon
                                    style={{
                                        fontSize: 16,
                                        color: statusConfig.icon.color,
                                    }}
                                />
                            )}
                        </div>
                    )}
                </Container>
            </AccordionSummary>
            {matchingValue && (
                <AccordionDetails>
                    <Container
                        style={{
                            flexDirection: 'column',
                            gap: theme.spacing(2),
                        }}
                    >
                        <Container
                            style={{
                                flexDirection: 'column',
                                gap: theme.spacing(1),
                            }}
                        >
                            <Container
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: theme.spacing(2),
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    style={{
                                        width: 100,
                                        color: theme.palette.text.secondary,
                                        textTransform: 'uppercase',
                                        fontWeight: 500,
                                    }}
                                >
                                    Document:
                                </Typography>
                                <Typography variant="body1">
                                    {matchingValue.document_value || 'No value'}
                                </Typography>
                            </Container>
                            <Container
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: theme.spacing(2),
                                    cursor: 'pointer',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    style={{
                                        width: 100,
                                        color: theme.palette.text.secondary,
                                        textTransform: 'uppercase',
                                        fontWeight: 500,
                                    }}
                                >
                                    Rent Roll:
                                </Typography>
                                <Typography variant="body1">
                                    {matchingValue.rent_roll_value ||
                                        'No value'}
                                </Typography>
                            </Container>
                        </Container>

                        <Container
                            style={{
                                flexDirection: 'column',
                                gap: theme.spacing(1),
                            }}
                        >
                            <Typography
                                variant="caption"
                                style={{
                                    color: theme.palette.text.secondary,
                                    textTransform: 'uppercase',
                                    fontWeight: 500,
                                }}
                            >
                                Supporting Values
                            </Typography>
                            {supportingValues.map((value) => (
                                <SupportingValue
                                    key={value.id}
                                    value={value}
                                    theme={theme}
                                    onValueClick={onDocumentSelect}
                                    isSelected={
                                        value.document.id ===
                                            selectedDocumentId &&
                                        value.page_number === selectedPage &&
                                        value.id === selectedValueId &&
                                        value.coordinates ===
                                            selectedCoordinates
                                    }
                                />
                            ))}
                            {supportingValues.length === 0 && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    No supporting values found
                                </Typography>
                            )}
                        </Container>
                    </Container>
                </AccordionDetails>
            )}
        </Accordion>
    )
}

export const EntityList = ({
    criteriaGroup,
    row,
    entityValue,
    onEntitySelect,
    onDocumentSelect,
    theme,
    selectedDocumentId,
    selectedPage,
    selectedValueId,
    selectedCoordinates,
    onOpenChat,
}: Props) => {
    return (
        <Paper elevation={1} style={{ width: 400, overflowY: 'auto' }}>
            {criteriaGroup?.match_criteria
                .filter(
                    (criteria) =>
                        (criteria.display ||
                            criteria.entity_class.always_display) &&
                        row?.values.some(
                            (v) =>
                                v.entity_classification.id ===
                                criteria.entity_class.id,
                        ),
                )
                .map((criteria) => {
                    const matchingValue = row?.values.find(
                        (v) =>
                            v.entity_classification.id ===
                            criteria.entity_class.id,
                    )

                    return (
                        <EntityListItem
                            key={criteria.id}
                            criteria={criteria}
                            matchingValue={matchingValue}
                            entityValue={entityValue}
                            onEntitySelect={onEntitySelect}
                            theme={theme}
                            row={row}
                            onDocumentSelect={onDocumentSelect}
                            selectedDocumentId={selectedDocumentId}
                            selectedPage={selectedPage}
                            selectedValueId={selectedValueId}
                            selectedCoordinates={selectedCoordinates}
                            onOpenChat={onOpenChat}
                        />
                    )
                })}
        </Paper>
    )
}
